import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Alert,
  Snackbar,
  Grid,
  Paper,
  Divider,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ReCAPTCHA from "react-google-recaptcha";
import {
  getFirestore,
  collection,
  getDocs,
  query,
  orderBy,
  addDoc,
  doc,
  getDoc,
  where,
} from "firebase/firestore";
import { getApp } from "firebase/app";
import { db } from "../../config/firebase";
import axios from "axios";
import useTechPrepEmail from "./hooks/useTechPrepEmail";

// ColdFusion TechPrep email endpoint (can be overridden via env).
// By default we rely on the app axios configuration (useAxiosConfig) which builds
// the full URL (BASE_URL + APP_NAME + apiKey). If a full URL is provided via
// REACT_APP_TECHPREP_EMAIL_URL we will display/use that; otherwise show the
// internal endpoint name used by the app ('TechPrepShowcaseEmail').
const TECHPREP_EMAIL_URL = process.env.REACT_APP_TECHPREP_EMAIL_URL || null;
const TECHPREP_EMAIL_DISPLAY = TECHPREP_EMAIL_URL
  ? TECHPREP_EMAIL_URL
  : "TechPrepShowcaseEmail (via app API)";

const TechPrepShowcaseForm = () => {
  // Cloud Functions URLs for 2nd Gen functions
  const FUNCTIONS_URLS = {
    getSites: "https://getsites-vatc3noraa-uc.a.run.app",
    getInstructors: "https://getinstructors-vatc3noraa-uc.a.run.app",
    // submitTechPrepShowcase removed (unused) — submissions are written to Firestore
    // Optional: public Cloud Function URL that returns showcase categories as JSON
    // Configure via REACT_APP_GET_SHOWCASE_CATEGORIES_URL in environment for public access
    getShowcaseCategories:
      process.env.REACT_APP_GET_SHOWCASE_CATEGORIES_URL || null,
  };

  // Hook for sending Tech Prep emails via ColdFusion endpoint
  const {
    send: sendTechEmail,
    loading: sendingEmail,
    error: sendEmailError,
  } = useTechPrepEmail();

  // Temporary debug state to manually trigger and observe email send behavior
  const [debugEmailStatus, setDebugEmailStatus] = useState("idle"); // idle|sending|success|error
  const [debugEmailMessage, setDebugEmailMessage] = useState("");

  // Manual test helper to invoke the email hook from the UI (used by debug button)
  const handleTestEmail = async () => {
    const testPayload = {
      toEmail: formData.teamEmail || "test@example.com",
      subject: `TEST Tech Prep Showcase - ${new Date().toISOString()}`,
      body: "This is a test email sent from the UI to validate the ColdFusion endpoint.",
      jsStruct: { showcaseId: "TEST1234", projectTitle: "Test Project" },
    };

    try {
      setDebugEmailStatus("sending");
      setDebugEmailMessage("Posting to: " + TECHPREP_EMAIL_DISPLAY);
      console.log(
        "handleTestEmail: invoking sendTechEmail with payload:",
        testPayload,
      );
      // include debug flag so the CFC returns parsed payload and generated body instead of sending email
      const resp = await sendTechEmail({
        ...testPayload,
        jsStruct: JSON.stringify(testPayload.jsStruct || {}),
        debug: "1",
      });
      console.log("handleTestEmail: sendTechEmail response:", resp);
      setDebugEmailStatus("success");
      // Prefer server-provided debugEmailBody when present
      if (resp && resp.debugEmailBody) {
        const bodyPreview = resp.debugEmailBody;
        const parsed = resp.debugJsStruct
          ? JSON.stringify(resp.debugJsStruct)
          : "";
        setDebugEmailMessage(
          `Server parsed jsStruct: ${parsed} --\nGenerated email body:\n${bodyPreview}`,
        );
      } else {
        setDebugEmailMessage(JSON.stringify(resp));
      }
    } catch (err) {
      console.error("handleTestEmail: error", err);
      setDebugEmailStatus("error");
      setDebugEmailMessage(err && err.message ? err.message : String(err));

      // Fallback: attempt a simple GET to the CFC endpoint (we added GET support)
      try {
        const rawAuth =
          axios.defaults &&
          axios.defaults.headers &&
          axios.defaults.headers.common &&
          axios.defaults.headers.common.Authorization
            ? axios.defaults.headers.common.Authorization
            : "";
        const apiKey = rawAuth.replace(/^Bearer\s+/i, "").trim();
        const base = (process.env.REACT_APP_REST_URL || "").replace(/\/$/, "");
        const app = (process.env.REACT_APP_REST_APP_NAME || "").replace(
          /\/$/,
          "",
        );
        if (base && app && apiKey) {
          const params = new URLSearchParams();
          params.append("toEmail", testPayload.toEmail);
          params.append("jsStruct", JSON.stringify(testPayload.jsStruct || {}));
          // request debug via GET as well
          params.append("debug", "1");
          const url = `${base}/${app}/TechPrepShowcaseEmail/?apiKey=${encodeURIComponent(
            apiKey,
          )}&${params.toString()}`;
          setDebugEmailStatus("sending");
          setDebugEmailMessage("Fallback GET to: " + url);
          const fResp = await fetch(url, { method: "GET", mode: "cors" });
          const text = await fResp.text();
          if (fResp.ok) {
            setDebugEmailStatus("success");
            setDebugEmailMessage(text);
          } else {
            setDebugEmailStatus("error");
            setDebugEmailMessage(
              `Fallback GET failed: HTTP ${fResp.status} - ${text}`,
            );
          }
        } else {
          console.warn(
            "Fallback GET not attempted: missing REST config or apiKey",
          );
        }
      } catch (fbErr) {
        console.error("handleTestEmail fallback error", fbErr);
        setDebugEmailMessage(
          fbErr && fbErr.message ? fbErr.message : String(fbErr),
        );
      }
    }
  };

  const [formData, setFormData] = useState({
    siteOfDelivery: "",
    siteOfDeliveryId: "", // Store the SiteID for instructor filtering
    program: "",
    instructor: "",
    instructorId: "", // Store the instructor doc ID for pathway lookup
    pathwayName: "", // Auto-populated from PathwaysPrograms via instructor
    techPrepPartner: "", // Auto-populated from ProgramDelivery via instructor
    competitiveCategory: "",
    projectTitle: "",
    teamEmail: "",
    emailConfirmation: "",
    attendingInPerson: "yes",
    oversizedProject: "no", // NEW
    electricityRequired: "no", // NEW
    videoUrl: "",
    teamMembers: [
      { name: "", required: true },
      { name: "", required: true },
      { name: "", required: false },
    ],
  });

  const [errors, setErrors] = useState({});
  const [validationSummary, setValidationSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // New state for Firebase data
  const [sites, setSites] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [instructorsLoading, setInstructorsLoading] = useState(false);
  const [pathwayLoading, setPathwayLoading] = useState(false);

  // New state for competitive categories from Firestore
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Form enabled state controlled by admin via TpShowFormStatus collection
  const [formEnabled, setFormEnabled] = useState(null); // null = unknown/loading, true/false for enabled

  // reCAPTCHA
  const recaptchaRef = useRef(null);
  const [captchaValue, setCaptchaValue] = useState(null);

  // Options for dropdowns
  const programs = [
    "Accounting & Finance",
    "Agriculture",
    "Architecture & Construction",
    "Arts & Communications",
    "Business Administration",
    "Computer Information Technology",
    "Education & Human Services",
    "Engineering & Advanced Manufacturing",
    "Health Sciences",
    "Hospitality & Tourism",
    "Law & Public Safety",
    "Transportation",
  ];

  // Helper: normalize and dedupe category list into {id, f_pretty_cat}
  const normalizeCategories = (list) =>
    (list || [])
      .map((cat, idx) => ({
        id: cat.id || cat.ID || `cat-${idx}`,
        display: (
          cat.f_pretty_cat ||
          cat.competitive_category ||
          cat.CategoryName ||
          cat.name ||
          ""
        )
          .toString()
          .trim(),
        rawActive: (cat.Active !== undefined && cat.Active !== null
          ? String(cat.Active).trim()
          : cat.active !== undefined && cat.active !== null
            ? String(cat.active).trim()
            : ""
        ).toLowerCase(),
      }))
      .filter((c) => c.display && c.display.length > 0)
      .filter((c) => !["0", "no", "false"].includes(c.rawActive))
      .reduce(
        (acc, cur) => {
          if (!acc.seen.has(cur.display)) {
            acc.seen.add(cur.display);
            acc.items.push(cur);
          }
          return acc;
        },
        { seen: new Set(), items: [] },
      )
      .items.sort((a, b) => a.display.localeCompare(b.display))
      .map((c) => ({ id: c.id, f_pretty_cat: c.display }));

  // Remote categories loader — should never require a siteId. Use only the configured public URL and log full server responses for debugging.
  const loadRemoteCategories = async () => {
    if (!FUNCTIONS_URLS.getShowcaseCategories) {
      console.warn(
        "REACT_APP_GET_SHOWCASE_CATEGORIES_URL not configured; skipping remote categories fetch",
      );
      return 0;
    }

    const url = FUNCTIONS_URLS.getShowcaseCategories;
    let lastError = null;

    try {
      setCategoriesLoading(true);
      console.log("Attempting to load categories from:", url);
      const resp = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
      });

      if (!resp.ok) {
        // Try to read body for server error details (some deployed functions return JSON/text explaining missing params)
        let bodyText = "";
        try {
          bodyText = await resp.text();
        } catch (e) {
          bodyText = `<unreadable response: ${e.message}>`;
        }
        console.warn(
          `Request to ${url} returned status ${resp.status}: ${bodyText}`,
        );
        lastError = new Error(`HTTP ${resp.status}: ${bodyText}`);
        return 0;
      }

      const data = await resp.json();

      // Normalize many possible shapes returned by the public function:
      // - Array directly
      // - { categories: [...] } or { result: [...] }
      // - { siteId1: [...], siteId2: [...] } -> flatten to all arrays
      let remote = [];
      if (Array.isArray(data)) {
        remote = data;
      } else if (Array.isArray(data.categories)) {
        remote = data.categories;
      } else if (Array.isArray(data.result)) {
        remote = data.result;
      } else if (data && typeof data === "object") {
        // Flatten any object values that are arrays (handles siteId -> array mapping)
        Object.values(data).forEach((v) => {
          if (Array.isArray(v)) remote = remote.concat(v);
          else if (v && Array.isArray(v.categories))
            remote = remote.concat(v.categories);
        });
      }

      // Deduplicate and normalize
      const mapped = normalizeCategories(remote);
      console.log(
        "Public function categories loaded (all):",
        mapped.length,
        "from",
        url,
      );
      setCategories(mapped);
      return mapped.length;
    } catch (err) {
      console.error("Error loading categories from", url, err);
      lastError = err;
      setSnackbarMessage(
        "Unable to load competitive categories from public service. Falling back to Firestore...",
      );
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return 0;
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Load competitive categories from Firestore ShowcaseCategories
  useEffect(() => {
    const loadCategories = async () => {
      // If a public function is configured, try it first (no siteId required)
      if (FUNCTIONS_URLS.getShowcaseCategories) {
        const remoteCount = await loadRemoteCategories();
        if (remoteCount > 0) return;
      }

      try {
        setCategoriesLoading(true);

        // Fetch all ShowcaseCategories documents
        const app = getApp();
        const firestore = getFirestore(app);
        const colRef = collection(firestore, "ShowcaseCategories");

        const snapshot = await getDocs(colRef);
        const raw = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        console.log("Loaded ShowcaseCategories docs (total):", raw.length);

        // Keep only categories explicitly marked Active === '-1' (string) or numeric -1 where possible
        const active = raw.filter((cat) => {
          const val =
            cat.Active !== undefined && cat.Active !== null
              ? String(cat.Active).trim()
              : cat.active !== undefined && cat.active !== null
                ? String(cat.active).trim()
                : "";
          return val.toLowerCase() === "-1";
        });

        let normalized = normalizeCategories(active);

        // Fallback: if no active categories found, try any docs that contain category fields but exclude explicitly inactive ones
        if (normalized.length === 0) {
          console.warn(
            "No active categories found; falling back to any categories present (excluding explicit inactive)",
          );
          normalized = normalizeCategories(raw);
        }

        console.log("Final categories to set:", normalized.length);
        setCategories(normalized);
      } catch (error) {
        console.error(
          "Error loading competitive categories (Firestore). Attempting fallback:",
          error,
        );

        setSnackbarMessage(
          "Unable to load competitive categories. Please contact the administrator for assistance.",
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setCategoriesLoading(false);
      }
    };

    loadCategories();
  }, []);

  // Load sites from Firebase via Cloud Function
  useEffect(() => {
    const loadSites = async () => {
      try {
        console.log("Starting to load sites via Cloud Function...");
        setSitesLoading(true);

        const response = await fetch(FUNCTIONS_URLS.getSites);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Sites data received:", data.sites);

        if (!data.sites || data.sites.length === 0) {
          console.log("No sites found");
          setSnackbarMessage("No sites found.");
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
        } else {
          setSites(data.sites);
        }
      } catch (error) {
        console.error("Error loading sites:", error);
        setSnackbarMessage(
          "Unable to load sites. Please contact the administrator for assistance.",
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setSitesLoading(false);
      }
    };

    loadSites();
  }, []);

  // Load instructors when site is selected via Cloud Function
  const loadInstructors = async (siteId) => {
    try {
      console.log("loadInstructors: starting for siteId=", siteId);
      setInstructorsLoading(true);

      const url = `${FUNCTIONS_URLS.getInstructors}?siteId=${siteId}`;
      console.log("loadInstructors: fetching URL=", url);

      const response = await fetch(url);

      console.log(
        "loadInstructors: fetch completed. ok=",
        response.ok,
        "status=",
        response.status,
      );

      if (!response.ok) {
        let txt = "";
        try {
          txt = await response.text();
        } catch (e) {
          txt = `<unreadable: ${e.message}>`;
        }
        console.warn("loadInstructors: non-ok response body:", txt);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("loadInstructors: raw response JSON=", data);
      console.log(
        "loadInstructors: keys in response=",
        Object.keys(data || {}),
      );

      // Some backends may return `users`, others `instructors` – normalize here
      const allUsers = data.users || data.instructors || [];
      console.log(
        "loadInstructors: total users from service=",
        allUsers.length,
      );
      console.log(
        "loadInstructors: sample users (first 10)=",
        allUsers.slice(0, 10),
      );

      // Log common fields to inspect shapes
      const sodSamples = allUsers.slice(0, 20).map((u) => ({
        id: u.id || u.ID || u.userId || "(no id)",
        displayName:
          u.displayName || u.name || u.DisplayName || "(no displayName)",
        SOD_Site_ID: u.SOD_Site_ID || u.SOD_SiteId || u.siteId || u.SOD_Site_ID,
      }));
      console.log("loadInstructors: user field samples=", sodSamples);

      // Filter users whose SOD_Site_ID matches the selected SiteID
      const filteredInstructors = allUsers.filter((user) => {
        const userSite =
          user.SOD_Site_ID ??
          user.SOD_SiteId ??
          user.siteId ??
          user.SOD_Site_ID;
        const match = String(userSite) === String(siteId);
        if (!match) {
          // Helpful debugging: show mismatch reasons for first few
          // (don't spam logs for every user)
        }
        return match;
      });

      console.log(
        "loadInstructors: filteredInstructors count=",
        filteredInstructors.length,
      );
      console.log(
        "loadInstructors: filteredInstructors sample=",
        filteredInstructors.slice(0, 10),
      );

      setInstructors(filteredInstructors);
    } catch (error) {
      console.error("Error loading instructors:", error);
      setSnackbarMessage("Error loading instructors. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setInstructorsLoading(false);
    }
  };

  // Lookup PathwayName from PathwaysPrograms via instructor → DeliveryDetails → ProgramDelivery → PathwaysPrograms
  // Chain: users(InstructorID) → DeliveryDetails(InstructorID→ProgramDeliveryID) → ProgramDelivery(ProgramDeliveryID→PathwayID) → PathwaysPrograms(PathwayID→PathwayName)
  const lookupPathwayForInstructor = async (instructorId) => {
    if (!instructorId) {
      setFormData((prev) => ({ ...prev, pathwayName: "", techPrepPartner: "" }));
      return;
    }
    try {
      setPathwayLoading(true);
      const firestore = getFirestore(getApp());

      console.group(`🔍 Pathway lookup for InstructorID: "${instructorId}"`);

      // Step 1: Find DeliveryDetails records where InstructorID matches
      // Query both string and number variants since Firestore is type-strict
      const instStr = String(instructorId);
      const instNum = parseInt(instructorId, 10);
      console.log(
        "Step 1: Querying DeliveryDetails where InstructorID ==",
        instStr,
        "(string) and",
        instNum,
        "(number)",
      );

      const [ddSnapStr, ddSnapNum] = await Promise.all([
        getDocs(
          query(
            collection(firestore, "DeliveryDetails"),
            where("InstructorID", "==", instStr),
          ),
        ),
        isNaN(instNum)
          ? Promise.resolve({ empty: true, docs: [] })
          : getDocs(
              query(
                collection(firestore, "DeliveryDetails"),
                where("InstructorID", "==", instNum),
              ),
            ),
      ]);

      const ddDocs = [
        ...ddSnapStr.docs,
        ...ddSnapNum.docs.filter(
          (d) => !ddSnapStr.docs.find((s) => s.id === d.id),
        ),
      ];

      if (ddDocs.length === 0) {
        // Also fetch a sample of DeliveryDetails to show what InstructorID values actually exist
        const sampleSnap = await getDocs(
          query(collection(firestore, "DeliveryDetails")),
        );
        const sampleIds = [
          ...new Set(
            sampleSnap.docs
              .slice(0, 20)
              .map((d) => d.data().InstructorID)
              .filter((v) => v !== undefined),
          ),
        ];
        console.warn(
          "❌ Step 1 FAILED: No DeliveryDetails docs found with InstructorID ==",
          instructorId,
          "(tried both string and number)",
        );
        console.warn(
          "   Sample InstructorID values in DeliveryDetails (first 20 docs):",
          sampleIds,
        );
        console.warn(
          "   ➡ The instructor's InstructorID field must match one of these values.",
        );
        console.groupEnd();
        setFormData((prev) => ({ ...prev, pathwayName: "", techPrepPartner: "" }));
        return;
      }

      const allDd = ddDocs.map((d) => ({ id: d.id, ...d.data() }));
      console.log(
        "✅ Step 1 PASSED: Found",
        allDd.length,
        "DeliveryDetails doc(s):",
        allDd.map((d) => ({
          docId: d.id,
          InstructorID: d.InstructorID,
          ProgramDeliveryID: d.ProgramDeliveryID,
          Active: d.Active,
        })),
      );

      const deliveryDetail = allDd[0];
      const programDeliveryId = deliveryDetail.ProgramDeliveryID;

      if (!programDeliveryId) {
        console.warn(
          "❌ Step 1→2 FAILED: DeliveryDetails doc found but ProgramDeliveryID is empty/missing",
        );
        console.warn("   Full doc data:", deliveryDetail);
        console.groupEnd();
        setFormData((prev) => ({ ...prev, pathwayName: "", techPrepPartner: "" }));
        return;
      }

      // Step 2: Find ProgramDelivery record by ProgramDeliveryID field
      console.log(
        "Step 2: Querying ProgramDelivery where ProgramDeliveryID ==",
        programDeliveryId,
      );
      const pdQuery = query(
        collection(firestore, "ProgramDelivery"),
        where("ProgramDeliveryID", "==", programDeliveryId),
      );
      const pdSnap = await getDocs(pdQuery);

      if (pdSnap.empty) {
        const sampleSnap = await getDocs(
          query(collection(firestore, "ProgramDelivery")),
        );
        const sampleIds = [
          ...new Set(
            sampleSnap.docs
              .slice(0, 20)
              .map((d) => d.data().ProgramDeliveryID)
              .filter(Boolean),
          ),
        ];
        console.warn(
          "❌ Step 2 FAILED: No ProgramDelivery docs found with ProgramDeliveryID ==",
          programDeliveryId,
        );
        console.warn(
          "   Sample ProgramDeliveryID values in ProgramDelivery collection (first 20 docs):",
          sampleIds,
        );
        console.warn(
          "   ➡ Check: Does ProgramDeliveryID",
          programDeliveryId,
          "exist in the ProgramDelivery collection?",
        );
        console.groupEnd();
        setFormData((prev) => ({ ...prev, pathwayName: "", techPrepPartner: "" }));
        return;
      }

      const allPd = pdSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      console.log(
        "✅ Step 2 PASSED: Found",
        allPd.length,
        "ProgramDelivery doc(s):",
        allPd.map((d) => ({
          docId: d.id,
          ProgramDeliveryID: d.ProgramDeliveryID,
          PathwayID: d.PathwayID,
          SiteID: d.SiteID,
        })),
      );

      const programDelivery = allPd[0];
      const pathwayId = programDelivery.PathwayID;
      // The "Tech Prep Partner" is the ProgramDelivery record's display name
      // (see ProgramDeliveryDropdown, which labels this same field "Select a Tech Prep Partner")
      const resolvedPartner = programDelivery.SOD_NameForApp || "";
      console.log("🤝 Resolved Tech Prep Partner:", resolvedPartner);

      if (!pathwayId) {
        console.warn(
          "❌ Step 2→3 FAILED: ProgramDelivery doc found but PathwayID is empty/missing",
        );
        console.warn("   Full doc data:", programDelivery);
        console.groupEnd();
        setFormData((prev) => ({
          ...prev,
          pathwayName: "",
          techPrepPartner: resolvedPartner,
        }));
        return;
      }

      // Step 3: Find PathwaysPrograms record by PathwayID field
      console.log(
        "Step 3: Querying PathwaysPrograms where PathwayID ==",
        pathwayId,
      );
      const ppQuery = query(
        collection(firestore, "PathwaysPrograms"),
        where("PathwayID", "==", pathwayId),
      );
      const ppSnap = await getDocs(ppQuery);

      if (ppSnap.empty) {
        const sampleSnap = await getDocs(
          query(collection(firestore, "PathwaysPrograms")),
        );
        const sampleIds = [
          ...new Set(
            sampleSnap.docs
              .slice(0, 20)
              .map((d) => d.data().PathwayID)
              .filter(Boolean),
          ),
        ];
        console.warn(
          "❌ Step 3 FAILED: No PathwaysPrograms docs found with PathwayID ==",
          pathwayId,
        );
        console.warn(
          "   Sample PathwayID values in PathwaysPrograms collection (first 20 docs):",
          sampleIds,
        );
        console.warn(
          "   ➡ Check: Does PathwayID",
          pathwayId,
          "exist in the PathwaysPrograms collection?",
        );
        console.groupEnd();
        setFormData((prev) => ({
          ...prev,
          pathwayName: "",
          techPrepPartner: resolvedPartner,
        }));
        return;
      }

      const pathway = ppSnap.docs[0].data();
      console.log("✅ Step 3 PASSED: Found PathwaysPrograms doc:", {
        PathwayID: pathway.PathwayID,
        PathwayName: pathway.PathwayName,
        PathwayNameShort: pathway.PathwayNameShort,
      });

      const resolvedPathwayName =
        pathway.PathwayName || pathway.PathwayNameShort || "";
      console.log("🎉 Resolved PathwayName:", resolvedPathwayName);
      console.groupEnd();
      setFormData((prev) => ({
        ...prev,
        pathwayName: resolvedPathwayName,
        techPrepPartner: resolvedPartner,
      }));
    } catch (err) {
      console.error("Error looking up pathway for instructor:", err);
      setFormData((prev) => ({ ...prev, pathwayName: "", techPrepPartner: "" }));
    } finally {
      setPathwayLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    // When instructor changes, capture instructor ID and trigger pathway lookup
    if (name === "instructor") {
      const selectedInstructor = instructors.find(
        (inst) => inst.displayName === value,
      );
      const instDocId = selectedInstructor ? selectedInstructor.id : "";
      // InstructorID (numeric) is the field used in DeliveryDetails — prefer it over the Firestore doc ID
      const instNumericId = selectedInstructor
        ? (selectedInstructor.InstructorID ??
          selectedInstructor.instructorID ??
          selectedInstructor.id)
        : "";
      console.log("Instructor selected:", {
        displayName: value,
        firestoreDocId: instDocId,
        numericInstructorID: instNumericId,
        fullObject: selectedInstructor,
      });
      setFormData((prev) => ({
        ...prev,
        instructor: value,
        instructorId: instDocId,
        pathwayName: "", // clear until lookup completes
        techPrepPartner: "", // clear until lookup completes
      }));
      if (instNumericId) lookupPathwayForInstructor(instNumericId);
      if (errors.instructor) setErrors((prev) => ({ ...prev, instructor: "" }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
      // Clear validation summary when user addresses a specific field
      setValidationSummary((prev) => {
        // If there are still other errors, keep them; otherwise clear
        const remaining = Object.keys(errors).filter(
          (k) => k !== name && errors[k],
        );
        return remaining.length > 0 ? prev : [];
      });
    }

    // Real-time email confirmation validation
    if (
      name === "emailConfirmation" &&
      formData.teamEmail &&
      value !== formData.teamEmail
    ) {
      setErrors((prev) => ({
        ...prev,
        emailConfirmation: "Email addresses do not match",
      }));
    } else if (
      name === "teamEmail" &&
      formData.emailConfirmation &&
      value !== formData.emailConfirmation
    ) {
      setErrors((prev) => ({
        ...prev,
        emailConfirmation: "Email addresses do not match",
      }));
    } else if (
      (name === "emailConfirmation" || name === "teamEmail") &&
      formData.teamEmail === value
    ) {
      setErrors((prev) => ({
        ...prev,
        emailConfirmation: "",
      }));
    }
  };

  // Special handler for site selection
  const handleSiteChange = (event) => {
    const selectedSiteName = event.target.value;
    const selectedSite = sites.find(
      (site) => site.SiteName === selectedSiteName,
    );

    console.log("handleSiteChange: selectedSiteName=", selectedSiteName);
    console.log("handleSiteChange: resolved selectedSite=", selectedSite);

    setFormData((prev) => ({
      ...prev,
      siteOfDelivery: selectedSiteName,
      siteOfDeliveryId: selectedSite ? selectedSite.SiteID : "",
      instructor: "", // Clear instructor when site changes
      competitiveCategory: "", // Clear competitive category when site changes
    }));

    // Clear error when user makes selection
    if (errors.siteOfDelivery) {
      setErrors((prev) => ({
        ...prev,
        siteOfDelivery: "",
      }));
      setValidationSummary((prev) => {
        const remaining = Object.keys(errors).filter(
          (k) => k !== "siteOfDelivery" && errors[k],
        );
        return remaining.length > 0 ? prev : [];
      });
    }

    // Load instructors only
    if (selectedSite && selectedSite.SiteID) {
      console.log(
        "handleSiteChange: calling loadInstructors with SiteID=",
        selectedSite.SiteID,
      );
      loadInstructors(selectedSite.SiteID);
      // Always fetch the full public category list (no siteId required)
      if (FUNCTIONS_URLS.getShowcaseCategories) {
        console.log(
          "Triggering public categories fetch (no siteId) functionUrl:",
          FUNCTIONS_URLS.getShowcaseCategories,
        );
        loadRemoteCategories();
      } else {
        console.warn(
          "Public getShowcaseCategories URL not configured (REACT_APP_GET_SHOWCASE_CATEGORIES_URL). Falling back to Firestore.",
        );
      }
    } else {
      setInstructors([]);
    }
  };

  const handleTeamMemberChange = (index, value) => {
    const newTeamMembers = [...formData.teamMembers];
    newTeamMembers[index].name = value;
    setFormData((prev) => ({
      ...prev,
      teamMembers: newTeamMembers,
    }));

    // Clear error for this team member
    if (errors[`teamMember${index}`]) {
      setErrors((prev) => ({
        ...prev,
        [`teamMember${index}`]: "",
      }));
      setValidationSummary((prev) => {
        const remaining = Object.keys(errors).filter(
          (k) => k !== `teamMember${index}` && errors[k],
        );
        return remaining.length > 0 ? prev : [];
      });
    }
  };

  const addTeamMember = () => {
    if (formData.teamMembers.length < 5) {
      setFormData((prev) => ({
        ...prev,
        teamMembers: [...prev.teamMembers, { name: "", required: false }],
      }));
    }
  };

  const removeTeamMember = (index) => {
    if (
      formData.teamMembers.length > 2 &&
      !formData.teamMembers[index].required
    ) {
      const newTeamMembers = formData.teamMembers.filter((_, i) => i !== index);
      setFormData((prev) => ({
        ...prev,
        teamMembers: newTeamMembers,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    if (!formData.siteOfDelivery)
      newErrors.siteOfDelivery = "Site of Delivery is required";
    if (!formData.instructor) newErrors.instructor = "Instructor is required";
    if (!formData.competitiveCategory)
      newErrors.competitiveCategory = "Competitive Category is required";
    if (!formData.projectTitle)
      newErrors.projectTitle = "Project Title is required";
    if (!formData.teamEmail) newErrors.teamEmail = "Team Email is required";
    if (!formData.emailConfirmation)
      newErrors.emailConfirmation = "Email Confirmation is required";

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.teamEmail && !emailRegex.test(formData.teamEmail)) {
      newErrors.teamEmail = "Please enter a valid email address";
    }
    if (
      formData.emailConfirmation &&
      !emailRegex.test(formData.emailConfirmation)
    ) {
      newErrors.emailConfirmation = "Please enter a valid email address";
    }

    // Email confirmation match validation
    if (
      formData.teamEmail &&
      formData.emailConfirmation &&
      formData.teamEmail !== formData.emailConfirmation
    ) {
      newErrors.emailConfirmation = "Email addresses must match";
    }

    // Team members validation (at least 2 required)
    const filledTeamMembers = formData.teamMembers.filter(
      (member) => member.name.trim() !== "",
    );
    if (filledTeamMembers.length < 2) {
      newErrors.teamMembers = "At least 2 team members are required";
    }

    // Individual team member validation
    formData.teamMembers.forEach((member, index) => {
      if (member.required && !member.name.trim()) {
        newErrors[`teamMember${index}`] =
          `Team Member ${index + 1} is required`;
      }
    });

    // reCAPTCHA validation
    if (!captchaValue) {
      newErrors.captcha = "Please complete the CAPTCHA verification";
    }

    // Build a human-friendly summary list for display
    const summaryList = Object.keys(newErrors).map((k) => newErrors[k]);
    setErrors(newErrors);
    setValidationSummary(summaryList);

    return newErrors; // return the errors object so caller can inspect
  };

  function generateShowcaseId() {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();

    const formErrors = validateForm();
    if (formErrors && Object.keys(formErrors).length > 0) {
      // Show concise snackbar and a visible error summary above the form
      const firstFew = Object.values(formErrors).slice(0, 5).join("; ");
      setSnackbarMessage(
        `Please fix the errors in the form: ${firstFew}${
          Object.keys(formErrors).length > 5
            ? ` (+${Object.keys(formErrors).length - 5} more)`
            : ""
        }`,
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);

    try {
      // Generate unique Showcase ID
      const showcaseId = generateShowcaseId();
      // Map formData to Firestore schema expected by admin UI
      const mappedData = {
        showcase_year: new Date().getFullYear().toString(),
        project_title: formData.projectTitle,
        tech_prep_partner: formData.techPrepPartner,
        tech_prep_SOD: formData.siteOfDelivery,
        tech_prep_pathway: formData.pathwayName,
        competitive_category: formData.competitiveCategory,
        manual_competitive_category: "", // Not present in form
        f_finalized_competitive_category: "", // Not present in form
        Instructor_ID: formData.instructor,
        f_finalized_competitive_group: "", // Not present in form
        f_generated_competitive_group: "", // Not present in form
        grouping: "", // Not present in form
        // Use explicit fallbacks so defaults always persist in Firestore
        // even if the user never interacts with the radio buttons
        attending_in_person: formData.attendingInPerson || "yes",
        medical_bioscience_paper: "", // Not present in form
        video_presentation_url:
          formData.attendingInPerson === "no" ? formData.videoUrl || "" : "",
        project_summary_question_1: "", // Not present in form
        project_summary_question_2: "", // Not present in form
        project_summary_question_3: "", // Not present in form
        electricity_required: formData.electricityRequired || "no",
        oversized_project: formData.oversizedProject || "no",
        team_member_1: formData.teamMembers[0]?.name || "",
        team_member_2: formData.teamMembers[1]?.name || "",
        team_member_3: formData.teamMembers[2]?.name || "",
        team_member_4: formData.teamMembers[3]?.name || "",
        team_member_5: formData.teamMembers[4]?.name || "",
        f_team_members: formData.teamMembers
          .map((m) => m.name)
          .filter(Boolean)
          .join(", "),
        student_email: formData.teamEmail,
        judge_team_number: "", // Not present in form
        table_number: "", // Not present in form
        grad_year: "", // Not present in form
        f_table_location: "", // Not present in form
        note_from_team_app_A: "", // Not present in form
        note_from_team_app_B: "", // Not present in form
        internal_note: "", // Not present in form
        showcase_team_password: "", // Not present in form
        updated_stamp: new Date().toISOString(),
        app_B_submitted: "", // Not present in form
        location: "", // Not present in form
        participation_status: "", // Not present in form
        showcase_ID_string: showcaseId,
        created_at: new Date().toISOString(),
        // Include recaptcha token for server-side verification (optional)
        recaptchaToken: captchaValue,
        // Mark emailSent false initially; Cloud Function will update this field
        emailSent: false,
        // Optionally include original form fields for reference
        // ...formData,
      };

      // Save to Firestore ShowcaseSubmissions
      console.log("Submitting ShowcaseSubmissions mappedData (client):", {
        // avoid logging sensitive tokens in console in production; include essential fields for debugging
        showcase_ID_string: mappedData.showcase_ID_string,
        student_email: mappedData.student_email,
        project_title: mappedData.project_title,
        competitive_category: mappedData.competitive_category,
        created_at: mappedData.created_at,
      });

      const docRef = await addDoc(
        collection(db, "ShowcaseSubmissions"),
        mappedData,
      );
      console.log(
        "Firestore write complete. ShowcaseSubmissions doc id:",
        docRef.id,
      );

      // Build email payload (use same data whether or not Firestore write succeeded)
      // Load the ShowcaseEmailTemplate doc so we can include header/dates in email
      let templateData = {};
      try {
        const tplRef = doc(db, "ShowcaseEmailTemplate", "main");
        const tplSnap = await getDoc(tplRef);
        if (tplSnap.exists()) {
          templateData = tplSnap.data();
        }
      } catch (tplErr) {
        console.warn("Unable to load ShowcaseEmailTemplate doc:", tplErr);
      }

      const emailPayload = {
        toEmail: mappedData.student_email,
        subject: `Tech Prep Showcase Submission - ${mappedData.showcase_ID_string}`,
        body: `Thank you for your Tech Prep Showcase submission. Your Showcase ID is ${mappedData.showcase_ID_string}.`,
        jsStruct: {
          // include the mapped Firestore record so the backend can render all fields
          ...mappedData,
          // avoid sending sensitive tokens
          recaptchaToken: undefined,
          // include team members array explicitly
          teamMembersArray: formData.teamMembers.map((m) => m.name),
          // include template fields (subject/header and dates) so CFC can render them
          templateSubject:
            templateData.subject || templateData.header || "Tech Prep Showcase",
          showcaseDate:
            templateData.showcaseDate || templateData.showcase_date || "",
          medicalDate:
            templateData.medicalDate ||
            templateData.medical_bioscience_date ||
            "",
          virtualDate:
            templateData.virtualDate || templateData.virtual_team_date || "",
          // URL links for the email body
          medBioUrl: templateData.medBioUrl || "",
          youTubeUrl: templateData.youTubeUrl || "",
          techPrepUrl: templateData.techPrepUrl || "https://mvtechprep.org",
        },
      };

      // Attempt email send but don't let it block overall success if Firestore write worked.
      let emailSent = false;
      try {
        console.log(
          "Sending email via useTechPrepEmail hook to:",
          TECHPREP_EMAIL_DISPLAY,
          emailPayload,
        );
        await sendTechEmail({
          toEmail: emailPayload.toEmail,
          subject: emailPayload.subject,
          body: emailPayload.body,
          jsStruct: JSON.stringify(emailPayload.jsStruct || {}),
        });
        emailSent = true;
        console.log("Email send request succeeded (hook).");
      } catch (err) {
        console.error("Email send failed (hook)", err);

        // Non-blocking: inform user but still treat form submission as success if Firestore write succeeded
        setSnackbarMessage(
          "Application submitted. Email delivery could not be completed immediately.",
        );
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);

        // Fallback: try direct GET to the CFC endpoint (reuse logic from handleTestEmail fallback)
        try {
          console.log(
            "Attempting fallback GET to TechPrepShowcaseEmail endpoint...",
          );
          const rawAuth =
            axios.defaults &&
            axios.defaults.headers &&
            axios.defaults.headers.common &&
            axios.defaults.headers.common.Authorization
              ? axios.defaults.headers.common.Authorization
              : "";
          const apiKey = rawAuth.replace(/^Bearer\s+/i, "").trim();
          const base = (process.env.REACT_APP_REST_URL || "").replace(
            /\/$/,
            "",
          );
          const app = (process.env.REACT_APP_REST_APP_NAME || "").replace(
            /\/$/,
            "",
          );
          if (base && app && apiKey) {
            const params = new URLSearchParams();
            params.append("toEmail", emailPayload.toEmail);
            params.append(
              "jsStruct",
              JSON.stringify(emailPayload.jsStruct || {}),
            );
            // request debug to get server parsing details in response
            params.append("debug", "1");

            const url = `${base}/${app}/TechPrepShowcaseEmail/?apiKey=${encodeURIComponent(
              apiKey,
            )}&${params.toString()}`;

            console.log("Fallback GET URL:", url);
            const fResp = await fetch(url, { method: "GET", mode: "cors" });
            const text = await fResp.text();
            if (fResp.ok) {
              console.log("Fallback GET succeeded:", text);
              setDebugEmailStatus("success");
              setDebugEmailMessage(text);
              emailSent = true;
            } else {
              console.warn("Fallback GET failed:", fResp.status, text);
              setDebugEmailStatus("error");
              setDebugEmailMessage(
                `Fallback GET failed: HTTP ${fResp.status} - ${text}`,
              );
            }
          } else {
            console.warn(
              "Fallback GET not attempted: missing REST config or apiKey",
            );
          }
        } catch (fbErr) {
          console.error("handleSubmit fallback GET error", fbErr);
          setDebugEmailStatus("error");
          setDebugEmailMessage(
            fbErr && fbErr.message ? fbErr.message : String(fbErr),
          );
        }
      }

      // Email delivery is handled server-side by the sendShowcaseEmailOnCreate Firestore onCreate trigger as a fallback.
      console.log(
        "Waiting for Cloud Function sendShowcaseEmailOnCreate to process the new doc and update emailSent/emailSendError fields. emailSent:",
        emailSent,
      );

      setSnackbarMessage("Application submitted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      // Reset form
      setFormData({
        siteOfDelivery: "",
        siteOfDeliveryId: "",
        program: "",
        instructor: "",
        instructorId: "",
        pathwayName: "",
        techPrepPartner: "",
        competitiveCategory: "",
        projectTitle: "",
        teamEmail: "",
        emailConfirmation: "",
        attendingInPerson: "yes",
        oversizedProject: "no",
        electricityRequired: "no",
        videoUrl: "",
        teamMembers: [
          { name: "", required: true },
          { name: "", required: true },
          { name: "", required: false },
        ],
      });
      setInstructors([]);
      setValidationSummary([]);
      setCaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSnackbarMessage("Error submitting application. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Check admin-controlled form status and redirect to login if disabled
  useEffect(() => {
    const checkFormStatus = async () => {
      try {
        // Use the app-level Firestore instance (avoids relying on an auth-bound `db` which may require login)
        const app = getApp();
        const firestore = getFirestore(app);
        const snap = await getDocs(collection(firestore, "TpShowFormStatus"));

        if (!snap.empty) {
          const d = snap.docs[0];
          // Be forgiving about the active field (case-insensitive)
          const activeVal = String(d.data().active || "").toLowerCase();
          const enabled =
            activeVal === "yes" || activeVal === "true" || activeVal === "-1";
          setFormEnabled(enabled);
          if (!enabled) {
            // Explicitly closed by admin
            setSnackbarMessage("The public showcase form is currently closed.");
            setSnackbarSeverity("info");
            setSnackbarOpen(true);
          }
        } else {
          // No status doc found — assume the public form should be open for outside users
          console.warn(
            "No TpShowFormStatus doc found; assuming public form is open.",
          );
          setFormEnabled(true);
        }
      } catch (err) {
        console.error("Error checking form status", err);
        // Fail-open: if we can't read the status (for example due to Firestore rules requiring auth),
        // allow the public form to be used instead of blocking access.
        setFormEnabled(true);
      }
    };

    checkFormStatus();
  }, []);

  // Note: intentionally not showing a snackbar when form status cannot be determined
  // to avoid alarming end users. We still default to an open form (fail-open) and
  // log the error for administrators to inspect.

  // While we are checking the form status, show a centered loader to avoid flashing the form
  if (formEnabled === null) {
    return (
      <Container maxWidth="md">
        <Box mt={8} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // If form is disabled, show a friendly closed message (no redirect)
  if (formEnabled === false) {
    return (
      <Container maxWidth="md">
        <Box mt={8} display="flex" flexDirection="column" alignItems="center">
          <Paper elevation={3} sx={{ width: "100%", p: 4 }}>
            <Typography variant="h5" align="center" gutterBottom>
              The Tech Prep Showcase form is currently closed
            </Typography>
            <Typography variant="body1" align="center" paragraph>
              The public application form is not accepting submissions at this
              time. If you are an administrator and believe this is an error,
              please contact support.
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => (window.location.href = "/")}
              >
                Return to Dashboard
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box mt={8} display="flex" flexDirection="column" alignItems="center">
        <Paper elevation={3} sx={{ width: "100%", p: 4 }}>
          <Typography variant="h4" align="center" gutterBottom color="primary">
            {`Tech Prep ${new Date().getFullYear()} Showcase Application`}
          </Typography>

          <Typography
            variant="body1"
            align="center"
            color="textSecondary"
            paragraph
          >
            Please complete all required fields to submit your Tech Prep
            Showcase application.
          </Typography>

          {/* Validation summary alert */}
          {validationSummary.length > 0 && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setValidationSummary([])}
            >
              <Typography variant="body2">
                Please fix the following errors:
              </Typography>
              <ul>
                {validationSummary.map((msg, idx) => (
                  <li key={idx}>
                    <Typography variant="body2">{msg}</Typography>
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Site of Delivery */}
              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.siteOfDelivery}>
                  <InputLabel>School name *</InputLabel>
                  <Select
                    name="siteOfDelivery"
                    value={formData.siteOfDelivery}
                    onChange={handleSiteChange}
                    label="School name *"
                    disabled={sitesLoading}
                  >
                    {sitesLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading sites...
                      </MenuItem>
                    ) : (
                      sites.map((site) => (
                        <MenuItem key={site.id} value={site.SiteName}>
                          {site.SiteName}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {errors.siteOfDelivery && (
                    <Typography variant="caption" color="error">
                      {errors.siteOfDelivery}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Instructor */}
              <Grid item xs={12} md={12}>
                <FormControl fullWidth error={!!errors.instructor}>
                  <InputLabel>Instructor *</InputLabel>
                  <Select
                    name="instructor"
                    value={formData.instructor}
                    onChange={handleInputChange}
                    label="Instructor *"
                    disabled={!formData.siteOfDelivery || instructorsLoading}
                  >
                    {!formData.siteOfDelivery ? (
                      <MenuItem disabled>Please select a site first</MenuItem>
                    ) : instructorsLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading instructors...
                      </MenuItem>
                    ) : instructors.length === 0 ? (
                      <MenuItem disabled>
                        No instructors found for this site
                      </MenuItem>
                    ) : (
                      instructors.map((instructor) => (
                        <MenuItem
                          key={instructor.id}
                          value={instructor.displayName}
                        >
                          {instructor.displayName}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {errors.instructor && (
                    <Typography variant="caption" color="error">
                      {errors.instructor}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Pathway Name - auto-populated from instructor selection */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Pathway Program"
                  value={
                    pathwayLoading
                      ? "Looking up pathway..."
                      : formData.pathwayName
                  }
                  disabled
                  variant="outlined"
                  helperText={
                    !formData.instructor
                      ? "Select an instructor to auto-populate this field"
                      : pathwayLoading
                        ? "Loading..."
                        : formData.pathwayName
                          ? "Auto-populated from instructor's program delivery record"
                          : "No pathway found for this instructor"
                  }
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: formData.pathwayName
                        ? "rgba(0, 0, 0, 0.87)"
                        : "rgba(0, 0, 0, 0.4)",
                    },
                  }}
                />
              </Grid>

              {/* Competitive Category */}
              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.competitiveCategory}>
                  <InputLabel>Competitive Category *</InputLabel>
                  <Select
                    name="competitiveCategory"
                    value={formData.competitiveCategory}
                    onChange={handleInputChange}
                    label="Competitive Category *"
                    disabled={categoriesLoading}
                  >
                    {categoriesLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading categories...
                      </MenuItem>
                    ) : categories.length === 0 ? (
                      <MenuItem disabled>
                        No competitive categories available
                      </MenuItem>
                    ) : (
                      categories.map((cat) => (
                        <MenuItem key={cat.id} value={cat.f_pretty_cat || ""}>
                          {cat.f_pretty_cat}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {errors.competitiveCategory && (
                    <Typography variant="caption" color="error">
                      {errors.competitiveCategory}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Project Title */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="projectTitle"
                  label="Project Title *"
                  value={formData.projectTitle}
                  onChange={handleInputChange}
                  error={!!errors.projectTitle}
                  helperText={
                    errors.projectTitle ||
                    "Provide a clear, descriptive title for your project"
                  }
                  variant="outlined"
                />
              </Grid>

              {/* Team Email */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="teamEmail"
                  label="Team Email *"
                  type="email"
                  value={formData.teamEmail}
                  onChange={handleInputChange}
                  error={!!errors.teamEmail}
                  helperText={errors.teamEmail}
                  variant="outlined"
                />
              </Grid>

              {/* Email Confirmation */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="emailConfirmation"
                  label="Email Confirmation *"
                  type="email"
                  value={formData.emailConfirmation}
                  onChange={handleInputChange}
                  error={!!errors.emailConfirmation}
                  helperText={errors.emailConfirmation}
                  variant="outlined"
                />
              </Grid>

              {/* Attending In Person */}
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">
                        Attending In Person *
                      </FormLabel>
                      <RadioGroup
                        row
                        name="attendingInPerson"
                        value={formData.attendingInPerson}
                        onChange={handleInputChange}
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  {/* Oversized Project */}
                  <Grid item xs={12} md={4}>
                    <FormControl component="fieldset" fullWidth>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <FormLabel component="legend">
                          Oversized Project
                        </FormLabel>
                        <Tooltip
                          title="Larger than 2 foot by 6 foot table"
                          placement="right"
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: {
                                fontSize: "1rem",
                                padding: "8px 12px",
                              },
                            },
                          }}
                        >
                          <HelpOutlineIcon
                            fontSize="small"
                            sx={{ color: "text.secondary", cursor: "pointer" }}
                          />
                        </Tooltip>
                      </Box>
                      <RadioGroup
                        row
                        name="oversizedProject"
                        value={formData.oversizedProject}
                        onChange={handleInputChange}
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  {/* Electricity Required */}
                  <Grid item xs={12} md={4}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">
                        Electricity Required
                      </FormLabel>
                      <RadioGroup
                        row
                        name="electricityRequired"
                        value={formData.electricityRequired}
                        onChange={handleInputChange}
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>

              {/* Video URL - only show if not attending in person */}
              {formData.attendingInPerson === "no" && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Teams not attending in person will be required to provide a
                    URL of their video presentation not to exceed 10 minutes.
                  </Alert>
                  <TextField
                    fullWidth
                    name="videoUrl"
                    label="Video Presentation URL"
                    value={formData.videoUrl}
                    onChange={handleInputChange}
                    error={!!errors.videoUrl}
                    helperText={
                      errors.videoUrl ||
                      "Please provide a URL to your video presentation (max 10 minutes)"
                    }
                    variant="outlined"
                  />
                </Grid>
              )}

              {/* Team Members Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Team Members (2-5 members required)
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Important: Teams must have 2-5 student members. Teamwork is a
                  key factor in judging.
                </Alert>

                {errors.teamMembers && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.teamMembers}
                  </Alert>
                )}

                {formData.teamMembers.map((member, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <TextField
                      fullWidth
                      label={`Team Member ${index + 1}${member.required ? " *" : ""}`}
                      value={member.name}
                      onChange={(e) =>
                        handleTeamMemberChange(index, e.target.value)
                      }
                      error={!!errors[`teamMember${index}`]}
                      helperText={errors[`teamMember${index}`]}
                      variant="outlined"
                    />
                    {!member.required && formData.teamMembers.length > 2 && (
                      <IconButton
                        color="error"
                        onClick={() => removeTeamMember(index)}
                        aria-label="Remove team member"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                ))}

                {formData.teamMembers.length < 5 && (
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addTeamMember}
                    variant="outlined"
                    sx={{ mt: 1 }}
                  >
                    Add Team Member
                  </Button>
                )}
              </Grid>

              {/* reCAPTCHA */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={
                      process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
                      "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
                    } // Test key fallback
                    onChange={(value) => {
                      setCaptchaValue(value);
                      // Clear captcha error when user completes it
                      if (errors.captcha) {
                        setErrors((prev) => ({
                          ...prev,
                          captcha: "",
                        }));
                        setValidationSummary([]);
                      }
                    }}
                    onExpired={() => setCaptchaValue(null)}
                    onErrored={() => setCaptchaValue(null)}
                  />
                </Box>
                {errors.captcha && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: "block", textAlign: "center" }}
                  >
                    {errors.captcha}
                  </Typography>
                )}
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Box sx={{ textAlign: "center", mt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{ minWidth: 200, py: 1.5 }}
                  >
                    {loading ? "Submitting..." : "Submit Application"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>

        {/* Snackbar for feedback */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default TechPrepShowcaseForm;
